import * as React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Label, Input, Button } from 'reactstrap';
import { FaRocket, FaInfoCircle } from 'react-icons/fa';

interface AIPublishModalProps {
    isOpen: boolean;
    toggle: () => void;
    configurationId: number | null;
    toolName: string;
    description: string;
    isClaimLevel: boolean;
    publishComments: string;
    isActionPending: boolean;
    onNameChange: (val: string) => void;
    onDescriptionChange: (val: string) => void;
    onClaimLevelChange: (val: boolean) => void;
    onCommentsChange: (val: string) => void;
    onConfirmPublish: () => void;
}

export const AIPublishModal: React.FC<AIPublishModalProps> = ({
    isOpen,
    toggle,
    configurationId,
    toolName,
    description,
    isClaimLevel,
    publishComments,
    isActionPending,
    onNameChange,
    onDescriptionChange,
    onClaimLevelChange,
    onCommentsChange,
    onConfirmPublish
}) => {
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="md" className="border-0">
            <ModalHeader toggle={toggle} className="border-bottom text-dark fw-bold">
                <FaRocket className="me-2 text-primary" /> {configurationId ? 'Publish Revised Tool Version' : 'Publish AI Tool'}
            </ModalHeader>
            <ModalBody className="p-4 bg-light">
                {configurationId && (
                    <div className="alert alert-info py-2 px-3 border-0 small shadow-sm fw-bold mb-3">
                        <FaInfoCircle className="me-2" /> Modifying Active Configuration Block ID: #{configurationId}
                    </div>
                )}
                <div className="mb-3">
                    <Label className="small fw-bold text-muted text-uppercase mb-1">Tool Name</Label>
                    <Input 
                        type="text"
                        value={toolName} // CONTROLLED: Reads directly from parent, zero lag
                        onChange={(e) => onNameChange(e.target.value)} 
                        className="fw-bold"
                        disabled={!!configurationId}
                    />
                </div>
                <div className="mb-3">
                    <Label className="small fw-bold text-muted text-uppercase mb-1">Description</Label>
                    <Input 
                        type="textarea"
                        rows={2}
                        value={description} // CONTROLLED: Reads directly from parent
                        onChange={(e) => onDescriptionChange(e.target.value)} 
                    />
                </div>
                <div className="mb-3 bg-white p-3 rounded shadow-sm border">
                    <div className="form-check form-switch">
                        <Input 
                            type="checkbox" 
                            id="claimLevelSwitch"
                            checked={isClaimLevel} 
                            onChange={(e) => onClaimLevelChange(e.target.checked)} 
                            className="form-check-input cursor-pointer" 
                        />
                        <Label check htmlFor="claimLevelSwitch" className="form-check-label fw-bold text-dark mb-0 ms-2 cursor-pointer">
                            Claim-Level Tool
                        </Label>
                    </div>
                    <p className="small text-muted mt-1 mb-0 ms-5">If enabled, this tool will appear on the Claim Detail screen and contextually inject claim data.</p>
                </div>
                <div className="mb-0">
                    <Label className="small fw-bold text-muted text-uppercase mb-1">Version Release Comments</Label>
                    <Input 
                        type="text"
                        placeholder={configurationId ? "e.g., Fixed schema bug, modified parameters..." : "e.g., Initial Release..."}
                        value={publishComments} // CONTROLLED: Reads directly from parent
                        onChange={(e) => onCommentsChange(e.target.value)} 
                    />
                </div>
            </ModalBody>
            <ModalFooter className="bg-white">
                <Button variant="secondary" onClick={toggle} disabled={isActionPending}>
                    CANCEL
                </Button>
                <Button variant="primary" onClick={onConfirmPublish} disabled={isActionPending}>
                    {isActionPending ? 'DEPLOYING...' : 'SAVE'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AIPublishModal;